import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { AddIcon, WalletIcon } from 'icons/materialIcons';
import TeamEventBadgesModal from './TeamEventBadgesModal';
import TeamWallet from './TeamWallet';
import { useQRCodeContext } from 'contexts/QrCodeContext';
import TeamQRCodeColorPicker from './TeamQRCodeColorPicker';
import TeamQrCodeCustomLogo from './TeamQrCodeCustomLogo';
import { useParams, useNavigate } from 'react-router-dom';
import TeamUpgradeSnackBar from 'team/shared/components/notification/TeamUpgradeSnackBar';
import { getUserTeams } from 'team/apis/teamApi';
import { getSubteam } from 'team/apis/teamSubteamApi';
import { useAuth } from 'contexts/AuthContext';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamQRCodeContainer = () => {
    const iconColor = "#828282";
    const outlineColor = '#dedede';
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLayout, setSelectedLayout] = useState('');
    const [showWallet, setShowWallet] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [hasChanges, setHasChanges] = useState(false);
    const [open, setOpen] = useState(false);  // for UpgradeSnackBar
    const { cardId: paramCardId } = useParams();
    const navigate = useNavigate();
    const { authState } = useAuth();
    const userId = authState?.user?.userId;

    // ✅ TEMPLATE CONTEXT: Use authenticated user's card if no cardId from params
    // Check if we're in template edit page by checking the URL path
    const isTemplateContext = !paramCardId || window.location.pathname.includes('/template/');
    const userCardId = authState.user?.activeCardId ||
        (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);
    const cardId = isTemplateContext ? userCardId : paramCardId;

    // QR Code restrictions state
    const [qrCodeRestrictions, setQrCodeRestrictions] = useState({
        lockAll: false,
        fields: {
            'Color': false,
            'Custom Logo': false
        }
    });
    const [allowChangeRequests, setAllowChangeRequests] = useState(false);
    const [loadingRestrictions, setLoadingRestrictions] = useState(false);
    const [isTeamAdmin, setIsTeamAdmin] = useState(false);
    const [isSubteamAdmin, setIsSubteamAdmin] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'warning'
    });

    const {
        qrCodeState,
        setQRCodeColor,
        setQRCodeLogoUrl,
        clearQRCodeSettings,
        initializeQRCode
    } = useQRCodeContext();

    // Initialize QR code context for the cardId if it doesn't exist
    useEffect(() => {
        if (cardId && !qrCodeState[cardId]) {
            initializeQRCode(cardId);
        }
    }, [cardId, qrCodeState, initializeQRCode]);

    // Load QR Code restrictions
    const loadQrCodeRestrictions = useCallback(async () => {
        if (!userId) return;

        setLoadingRestrictions(true);
        try {
            const teamsResponse = await getUserTeams();
            const teams = teamsResponse?.teams || teamsResponse || [];

            const subteamIds = [];
            let userIsTeamAdmin = false;
            let userIsSubteamAdmin = false;

            teams.forEach(team => {
                const membership = team.membershipInfo || team;
                if (membership.role === 'admin') {
                    userIsTeamAdmin = true;
                }
                if (membership.isSubteamAdmin) {
                    userIsSubteamAdmin = true;
                }
                if (membership.subteam?._id) {
                    subteamIds.push(membership.subteam._id);
                }
            });

            setIsTeamAdmin(userIsTeamAdmin);
            setIsSubteamAdmin(userIsSubteamAdmin);

            // If user is admin, no restrictions apply
            if (userIsTeamAdmin || userIsSubteamAdmin) {
                setLoadingRestrictions(false);
                return;
            }

            const mergedRestrictions = {
                lockAll: false,
                fields: {
                    'Color': false,
                    'Custom Logo': false
                }
            };

            // Load restrictions from all subteams (if any subteam restricts, it's restricted)
            let mergedAllowChangeRequests = false;
            for (const subteamId of subteamIds) {
                try {
                    const subteamResponse = await getSubteam(subteamId);
                    const subteam = subteamResponse?.subteam || subteamResponse;
                    const subteamRestrictions = subteam?.settings?.restrictions || {};

                    // Load allowChangeRequests from About restrictions (QR Code uses the same setting)
                    if (subteamRestrictions.about?.allowChangeRequests) {
                        mergedAllowChangeRequests = true;
                    }

                    if (subteamRestrictions.qrCode) {
                        const qrCodeRestrictions = subteamRestrictions.qrCode;
                        // If any subteam has lockAll, it's restricted
                        if (qrCodeRestrictions.lockAll) {
                            mergedRestrictions.lockAll = true;
                        }
                        // Merge field restrictions (if any subteam restricts a field, it's restricted)
                        if (qrCodeRestrictions.fields) {
                            Object.keys(mergedRestrictions.fields).forEach(field => {
                                if (qrCodeRestrictions.fields[field] === true) {
                                    mergedRestrictions.fields[field] = true;
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.error(`Error loading restrictions from subteam ${subteamId}:`, error);
                }
            }

            setAllowChangeRequests(mergedAllowChangeRequests);

            setQrCodeRestrictions(mergedRestrictions);
        } catch (error) {
            console.error('Error loading QR code restrictions:', error);
        } finally {
            setLoadingRestrictions(false);
        }
    }, [userId]);

    useEffect(() => {
        loadQrCodeRestrictions();
    }, [loadQrCodeRestrictions]);

    // Helper function to check if a QR code field is restricted
    const isQrCodeFieldRestricted = useCallback((fieldName) => {
        if (isTeamAdmin || isSubteamAdmin) return false; // Admins are not restricted
        if (qrCodeRestrictions.lockAll) return true; // If lockAll is enabled, all fields are restricted
        return qrCodeRestrictions.fields[fieldName] === true;
    }, [qrCodeRestrictions, isTeamAdmin, isSubteamAdmin]);

    // Helper function to check if change requests are allowed
    const canRequestChange = useCallback(() => {
        // QR Code restrictions use the same allowChangeRequests as About restrictions
        return allowChangeRequests;
    }, [allowChangeRequests]);

    const handleRestrictionNotification = useCallback((message) => {
        setNotification({
            open: true,
            message,
            severity: 'warning'
        });
    }, []);

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubscribe = () => {
        handleClose();
        navigate(`/dashboard/subscribe`);
    };

    useEffect(() => {
        if (cardId && qrCodeState[cardId]) {
            const currentColor = qrCodeState[cardId].contextQrCodeColor;
            const currentLogo = qrCodeState[cardId].contextQrCodeLogoUrl;

            setHasChanges(
                (currentColor && currentColor !== '#000000') ||
                (currentLogo !== null && currentLogo !== undefined)
            );
        }
    }, [cardId, qrCodeState]);

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleSelectionChange = (layout) => {
        setSelectedLayout(layout);
    };

    const handleAddToWallet = () => {
        setShowWallet(true);
    };

    const handleCloseWallet = () => {
        setShowWallet(false);
    };

    const handleCancel = () => {
        if (cardId) {
            setQRCodeColor(cardId, '#000000');
            setQRCodeLogoUrl(cardId, null);
            clearQRCodeSettings();
            setRefreshKey(prev => prev + 1);
            setHasChanges(false);
        }
    };

    // Commented out as part of temporary change to hide upgrade features
    // const handleSave = () => {
    //     setOpen(true);  // Show upgrade snackbar
    // };

    // Updated cancel button styles - now takes position of save button for better UI alignment
    const cancelButtonStyles = {
        textTransform: 'none',
        borderRadius: '8px',
        minWidth: { xs: '120px', sm: '150px' },
        marginTop: 4,
        marginLeft: 2,
        ...(hasChanges ? {
            backgroundColor: 'black', // Using the same style as save button since it's taking its place
            color: 'white',
            '&:hover': {
                backgroundColor: 'black',
            }
        } : {
            backgroundColor: '#e0e0e0',
            color: '#a9a9ac',
            '&:hover': {
                backgroundColor: '#e0e0e0',
            }
        })
    };

    // Commented out as part of temporary change to hide upgrade features
    // const saveButtonStyles = {
    //     textTransform: 'none', 
    //     borderRadius: '8px', 
    //     minWidth: { xs: '120px', sm: '150px' }, 
    //     marginTop: 4,
    //     marginLeft: 2,
    //     ...(hasChanges ? {
    //         backgroundColor: 'black',
    //         color: 'white',
    //         '&:hover': {
    //             backgroundColor: 'black',
    //         }
    //     } : {
    //         backgroundColor: '#e0e0e0',
    //         color: '#a9a9ac',
    //         '&:hover': {
    //             backgroundColor: '#e0e0e0',
    //         }
    //     })
    // };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    padding: 2,
                    width: '100%',
                    maxWidth: '500px',
                    margin: '15px auto',
                }}
            >
                {/* ... rest of your JSX remains the same ... */}
                <Typography
                    variant="h6"
                    sx={{
                        marginBottom: '20px',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                    }}
                >
                    QR Code
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon sx={{ color: iconColor }} />}
                        sx={{
                            textTransform: 'none',
                            borderColor: outlineColor,
                            borderRadius: '8px',
                            minWidth: { xs: '120px', sm: '150px' },
                            color: 'inherit',
                        }}
                        onClick={handleOpenModal}
                    >
                        Create Event Badge
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<WalletIcon sx={{ color: iconColor }} />}
                        sx={{
                            textTransform: 'none',
                            borderColor: outlineColor,
                            borderRadius: '8px',
                            minWidth: { xs: '120px', sm: '150px' },
                            color: 'inherit',
                        }}
                        onClick={handleAddToWallet}
                    >
                        Add To Wallet
                    </Button>
                </Box>

                {showWallet && <TeamWallet onClose={handleCloseWallet} />}

                <TeamEventBadgesModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onSelectionChange={handleSelectionChange}
                    selectedLayout={selectedLayout}
                    cardId={cardId}
                />

                <Box sx={{ marginTop: 6 }}>
                    <TeamQRCodeColorPicker
                        cardId={cardId}
                        key={`color-${refreshKey}`}
                        forceReset={refreshKey}
                        isColorRestricted={isQrCodeFieldRestricted('Color')}
                        canRequestChange={canRequestChange()}
                        onRestrictionNotification={handleRestrictionNotification}
                    />
                </Box>

                <Box sx={{ marginTop: 4 }}>
                    <TeamQrCodeCustomLogo
                        cardId={cardId}
                        key={`logo-${refreshKey}`}
                        forceReset={refreshKey}
                        isLogoRestricted={isQrCodeFieldRestricted('Custom Logo')}
                        canRequestChange={canRequestChange()}
                        onRestrictionNotification={handleRestrictionNotification}
                    />
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    width: '100%'
                }}>
                    {/* Only showing the Cancel button in place of the Save button */}
                    {/* Save QRCode button hidden - temporary change to hide upgrade features */}
                    <Button
                        variant="contained"
                        sx={cancelButtonStyles}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>

            {/* UpgradeSnackBar still included but will never be shown since handleSave() is not called */}
            <TeamUpgradeSnackBar
                open={open}
                handleClose={handleClose}
                handleSubscribe={handleSubscribe}
            />

            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
            />
        </>
    );
};

export default TeamQRCodeContainer;